
import * as React from 'react';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { IFacet } from '../../models/Facet';
import { IPageLoadTrackerProps, PageLoadTracker } from '../pageLoadTracker/PageLoadTracker';
import { IComponentDecorator, IDecoratedProps } from '../../utilities/components/Decorators';

/**
 * @public
 */
export interface ILayoutComponentProps {
  onLayoutRenderComplete: (pageLoadTime: number) => void;
}

/**
 * @public
 */
export type IDecorateWithPageLoadTrackerProps<TProps extends ILayoutComponentProps> =
  IDecoratedProps<TProps, ILayoutComponentProps, Pick<IPageLoadTrackerProps, 'itemKey'>>;

/**
 * @internal
 */
export function withPageLoadTracker(facets: IFacet<{}>[]):
  IComponentDecorator<ILayoutComponentProps, Pick<IPageLoadTrackerProps, 'itemKey'>> {
  return <P extends ILayoutComponentProps>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, ILayoutComponentProps, Pick<IPageLoadTrackerProps, 'itemKey'>>> => {
    const Component = component;

    interface ILayoutTrackerState {
      isLayoutRenderComplete: boolean;
      pageLoadTime: number | undefined;
    }

    type IProps = IDecoratedProps<P, ILayoutComponentProps, Pick<IPageLoadTrackerProps, 'itemKey'>>;

    class LayoutTracker extends React.PureComponent<IProps, ILayoutTrackerState> {
      constructor(props: IProps) {
        super(props);

        this.state = {
          isLayoutRenderComplete: false,
          pageLoadTime: undefined
        };
      }

      public render(): JSX.Element {
        const {
          itemKey
        } = this.props;

        return (
          <PageLoadTracker
            key='tracker'
            itemKey={ itemKey }
            facets={ facets }
            isReady={ this.state.isLayoutRenderComplete }
            pageLoadTime={ this.state.pageLoadTime }
          >
            <Component
              { ...this.props as P }
              onLayoutRenderComplete={ this._onLayoutRenderComplete }
            />
          </PageLoadTracker>
        );
      }

      public componentWillReceiveProps(nextProps: IProps): void {
        const {
          itemKey
        } = this.props;
        const {
          itemKey: nextItemKey
        } = nextProps;
        if (itemKey !== nextItemKey) {
          // in items view one page navigation or folder navigation, reset the previousIsLayoutRenderComplete state
          this.setState({
            isLayoutRenderComplete: false,
            pageLoadTime: undefined
          });
        }
      }

      @autobind
      private _onLayoutRenderComplete(pageLoadTime: number): void {
        this.setState({
          isLayoutRenderComplete: true,
          pageLoadTime: pageLoadTime
        });
      }
    }

    return LayoutTracker;
  };
}
